import type { FieldDefinition } from "../fields/types.js";
import type { LogicCondition, LogicEvaluationResult, LogicRule } from "./types.js";

/**
 * Pure conditional-logic evaluation — no DOM, no I/O, no clock. The same function runs in the
 * browser for responsiveness and on the server at submit, so a form cannot behave one way for the
 * applicant and another way when their answers are stored.
 */

const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
};

/**
 * Loose equality across the string/number boundary on purpose: a rule authored against the option
 * value "5" must still match a `number` field answered as 5. Booleans compare by their own value,
 * not by truthiness, so `false` does not read as "empty".
 */
const looseEquals = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a === "boolean" || typeof b === "boolean") return String(a) === String(b);
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (typeof a === "object" || typeof b === "object")
    return JSON.stringify(a) === JSON.stringify(b);
  return String(a) === String(b);
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const evaluateCondition = (condition: LogicCondition, values: Record<string, unknown>): boolean => {
  const actual = values[condition.field];

  switch (condition.operator) {
    case "is_empty":
      return isEmptyValue(actual);
    case "is_not_empty":
      return !isEmptyValue(actual);

    case "equals":
      return looseEquals(actual, condition.value);
    case "not_equals":
      return !looseEquals(actual, condition.value);

    case "contains": {
      // Meaningful for both a multi-select answer and a free-text answer.
      if (Array.isArray(actual)) return actual.some((entry) => looseEquals(entry, condition.value));
      if (typeof actual === "string" && typeof condition.value === "string") {
        return actual.toLowerCase().includes(condition.value.toLowerCase());
      }
      return false;
    }

    case "greater_than": {
      const left = asNumber(actual);
      const right = asNumber(condition.value);
      return left !== null && right !== null && left > right;
    }
    case "less_than": {
      const left = asNumber(actual);
      const right = asNumber(condition.value);
      return left !== null && right !== null && left < right;
    }

    case "is_one_of": {
      const candidates = Array.isArray(condition.value) ? condition.value : [condition.value];
      // An array answer matches if any selected entry is among the candidates.
      if (Array.isArray(actual)) {
        return actual.some((entry) =>
          candidates.some((candidate) => looseEquals(entry, candidate)),
        );
      }
      return candidates.some((candidate) => looseEquals(actual, candidate));
    }
  }
};

const ruleMatches = (rule: LogicRule, values: Record<string, unknown>): boolean => {
  const results = rule.conditions.map((condition) => evaluateCondition(condition, values));
  return rule.match === "any" ? results.some(Boolean) : results.every(Boolean);
};

/**
 * Circular-reference detection.
 *
 * A rule creates an edge from each field it *reads* to each field it *affects*. A cycle in that
 * graph means a rule's outcome feeds back into its own input — the classic "A shows B, B shows A"
 * that makes a form's behaviour depend on evaluation order. The builder warns on these
 * (docs/01-PRODUCT-SPEC.md §3.3) and `POST /forms/:id/draft/validate` rejects them; here we
 * identify the offending rules and skip them rather than looping.
 */
export const detectCircularRules = (rules: readonly LogicRule[]): string[] => {
  const edges = new Map<string, { to: string; ruleId: string }[]>();

  for (const rule of rules) {
    for (const condition of rule.conditions) {
      for (const effect of rule.effects) {
        if (effect.targetType !== "field") continue;
        const from = condition.field;
        const list = edges.get(from) ?? [];
        list.push({ to: effect.target, ruleId: rule.id });
        edges.set(from, list);
      }
    }
  }

  const circular = new Set<string>();
  const state = new Map<string, "visiting" | "done">();
  const pathRuleIds: string[] = [];

  const visit = (node: string): void => {
    const current = state.get(node);
    if (current === "done") return;
    if (current === "visiting") {
      // Every rule on the current path participates in the cycle.
      for (const id of pathRuleIds) circular.add(id);
      return;
    }

    state.set(node, "visiting");
    for (const edge of edges.get(node) ?? []) {
      pathRuleIds.push(edge.ruleId);
      visit(edge.to);
      pathRuleIds.pop();
    }
    state.set(node, "done");
  };

  for (const node of edges.keys()) visit(node);

  return [...circular];
};

/**
 * Evaluates every rule against the current answers.
 *
 * Effects are applied in rule order and later effects win, which makes the outcome deterministic
 * and independent of how the object happens to be keyed. Fields are visible by default: only an
 * explicit `hide` (or a `show` whose condition is not met) hides one.
 */
export const evaluateLogic = (
  rules: readonly LogicRule[],
  values: Record<string, unknown>,
  fields: readonly FieldDefinition[],
): LogicEvaluationResult => {
  const circularRuleIds = detectCircularRules(rules);
  const circular = new Set(circularRuleIds);

  const hiddenFields = new Set<string>();
  const hiddenSections = new Set<string>();
  const skippedPages = new Set<number>();
  const requiredOverrides: Record<string, boolean> = {};
  const computedValues: Record<string, unknown> = {};

  const fieldKeys = new Set(fields.map((field) => field.key));

  for (const rule of rules) {
    // A rule that participates in a cycle is skipped entirely rather than half-applied.
    if (circular.has(rule.id)) continue;

    const matched = ruleMatches(rule, values);

    for (const effect of rule.effects) {
      switch (effect.action) {
        case "show":
          if (effect.targetType === "field") {
            if (matched) hiddenFields.delete(effect.target);
            else hiddenFields.add(effect.target);
          } else if (effect.targetType === "section") {
            if (matched) hiddenSections.delete(effect.target);
            else hiddenSections.add(effect.target);
          }
          break;

        case "hide":
          if (!matched) break;
          if (effect.targetType === "field") hiddenFields.add(effect.target);
          else if (effect.targetType === "section") hiddenSections.add(effect.target);
          break;

        case "require":
          if (matched && effect.targetType === "field") requiredOverrides[effect.target] = true;
          break;

        case "unrequire":
          if (matched && effect.targetType === "field") requiredOverrides[effect.target] = false;
          break;

        case "set_value":
          if (matched && effect.targetType === "field")
            computedValues[effect.target] = effect.value;
          break;

        case "skip_page":
        case "jump_to_page": {
          if (!matched) break;
          const pageNumber = Number(effect.target);
          if (Number.isFinite(pageNumber)) skippedPages.add(pageNumber);
          break;
        }
      }
    }
  }

  // A field inside a hidden section is itself hidden — otherwise its value would still be
  // validated and stored while the applicant never saw it.
  for (const field of fields) {
    if (field.sectionKey && hiddenSections.has(field.sectionKey)) hiddenFields.add(field.key);
  }

  // A rule may target a field that has since been deleted; report only keys that still exist so
  // callers can pass the result straight to buildFormValidator.
  const liveHiddenFields = [...hiddenFields].filter((key) => fieldKeys.has(key));

  return {
    hiddenFieldKeys: liveHiddenFields,
    hiddenSectionKeys: [...hiddenSections],
    requiredOverrides,
    skippedPages: [...skippedPages].sort((a, b) => a - b),
    computedValues,
    circularRuleIds,
  };
};
